import { FvButton } from '@fv/client-components'
import { addressFuncs, useFormModelState } from '@fv/client-core'
import type { ClientAddressLookupResult } from '@fv/client-types'

import { ValidatedForm } from '@/components/inputs'
import { AddressInput } from '@/components/inputs/AddressInput'
import { ContactPhoneInput } from '@/components/inputs/ContactPhoneInput'
import { InputGroup, InputGroupWrapper } from '@/components/inputs/InputGroup'

import { HasPermission } from '../auth'
import { addressMapper } from './addressFuncs'
import type { FormAddress } from './types'
import { useAddressBookConfig, useCloseAddressBookEdit } from './useAddressBook'
import { useCreateAddress } from './useCreateAddress'
import { useDeleteAddress } from './useDeleteAddress'
import { useUpdateAddress } from './useUpdateAddress'

type Props = {
  initialValue?: ClientAddressLookupResult
}
export const BillingAddressForm = ({ initialValue }: Props) => {
  const closeAddressBookEdit = useCloseAddressBookEdit()
  const config = useAddressBookConfig()

  const createAddress = useCreateAddress(config)
  const updateAddress = useUpdateAddress(config)
  const deleteAddress = useDeleteAddress(config)
  const mutation = initialValue?._id ? updateAddress : createAddress

  const { register, value, setValue } = useFormModelState<FormAddress>({
    initialValue: addressMapper.buildFormAddress(initialValue),
  })
  const handleSubmit = async () => {
    await mutation.mutateAsync({
      ...value,
      id: initialValue?._id,
      type: config.type,
    })
    closeAddressBookEdit()
  }

  const handleDelete = () => {
    if (!initialValue?._id) return
    if (
      window.confirm(
        'Are you sure you want to delete this entry from your address book?',
      )
    ) {
      deleteAddress.mutate(
        { id: initialValue._id },
        { onSuccess: () => closeAddressBookEdit() },
      )
    }
  }

  return (
    <ValidatedForm
      onValidSubmit={handleSubmit}
      className="flex h-full flex-col justify-between"
    >
      <div>
        <InputGroup
          required
          inputType="text"
          inputProps={register('company')}
          label="Company Name"
        />
        <div className="grid grid-cols-3 gap-x-2">
          <InputGroup
            required
            inputType="text"
            inputProps={register('address')}
            label="Address"
          />
          <InputGroup
            inputType="text"
            inputProps={register('address2')}
            label="Address 2"
          />
          <InputGroupWrapper
            name="cityStateZip"
            label="City, State, Zip"
            required
          >
            <AddressInput
              context={'address-book'}
              formatAddress={addressFuncs.cityStateZip}
              isClearable={false}
              location={value}
              name="cityStateZip"
              onLocationChange={x => {
                setValue(p => ({
                  ...p,
                  city: x?.city || '',
                  state: x?.state || '',
                  postalCode: x?.postalCode || '',
                  country: x?.country || 'us',
                  adminArea3: x?.adminArea3,
                }))
              }}
              placeholder="Zip/postal code"
            />
          </InputGroupWrapper>
        </div>
        <div className="grid grid-cols-3 gap-x-2">
          <InputGroup
            required
            inputType="text"
            inputProps={register('contactName')}
            label="Contact name"
          />
          <ContactPhoneInput
            required
            name="contactPhone"
            onChange={x => setValue(p => ({ ...p, ...x }))}
            values={{
              contactPhone: value.contactPhone || '',
              contactPhoneExt: value.contactPhoneExt || '',
            }}
          />
          <InputGroup
            inputType="text"
            inputProps={register('contactEmail')}
            label="Contact email"
          />
        </div>
      </div>

      <HasPermission name="manage-addresses">
        <div className="flyout-footer-actions flex items-center">
          <FvButton
            icon="check"
            theme="primary"
            type="submit"
            loading={mutation.isPending}
          >
            {initialValue?._id ? 'Update' : 'Add'} billing address
          </FvButton>
          {initialValue?._id && (
            <FvButton
              icon={deleteAddress.isPending ? 'spinner' : 'trash'}
              onClick={handleDelete}
              theme="default"
            >
              Delete address
            </FvButton>
          )}
          <FvButton
            loading={mutation.isPending}
            icon="times"
            theme="plain"
            type="button"
            onClick={closeAddressBookEdit}
          >
            Cancel
          </FvButton>
        </div>
      </HasPermission>
    </ValidatedForm>
  )
}
